import React, { FunctionComponent, useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { ScreenReaderUpdateText, SaveEmailSubmitButtonGroup } from 'components';
import { FormGroup } from 'components/chroma';

import { ApiData, SingleFieldStepProps, Extra } from 'scenes/common/types';

import OptionalExtrasList from './OptionalExtrasList';
import FindOutMoreModal from './FindOutMoreModal/FindOutMoreModal';

const EXTRAS_ERROR = 'extras-error';
const QUESTION = 'Add extras to your policy';

const OptionalExtrasStep: FunctionComponent<
  Pick<SingleFieldStepProps<string[]>, 'defaultStateValue'> & {
    availableExtras: Extra[];
    includedExtras?: string[];
    quoteDetails: ApiData<unknown>;
    onUpdateOptionalExtras: (extras: string[]) => void;
    onNext: SubmitHandler<Record<string, unknown>>;
    onClickFindOutMore: () => void;
    onClickSaveQuoteModal?: () => void;
  }
> = ({
  defaultStateValue,
  availableExtras,
  includedExtras,
  onNext,
  onUpdateOptionalExtras,
  onClickFindOutMore,
  onClickSaveQuoteModal,
}) => {
  const {
    formState: { isValid },
    errors,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
  });

  const [showModal, setShowModal] = useState(false);

  const handleSelect = useCallback(
    (optionalExtra: string): void => {
      if (includedExtras?.some((option) => option === optionalExtra)) {
        return;
      }

      const copySelectedExtras = [...defaultStateValue];
      const foundIndex = defaultStateValue?.findIndex(
        (extra) => extra === optionalExtra
      );

      if (foundIndex > -1) {
        copySelectedExtras.splice(foundIndex, 1);
      } else {
        copySelectedExtras.push(optionalExtra);
      }

      onUpdateOptionalExtras(copySelectedExtras);
    },
    [includedExtras, onUpdateOptionalExtras, defaultStateValue]
  );

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <FormGroup
        legend={QUESTION}
        error={errors[EXTRAS_ERROR]?.message || ''}
        helpLink={{
          id: 'find-out-more-link',
          text: 'Find out more about policy extras',
          ariaLabel: 'Find out more about policy extras - Opens dialog',
          onClick: () => {
            onClickFindOutMore();
            setShowModal(true);
          },
        }}
      >
        <ScreenReaderUpdateText />
        <OptionalExtrasList
          availableExtras={availableExtras}
          includedExtras={includedExtras}
          selectedExtras={defaultStateValue}
          onClick={handleSelect}
          numberOfOptions={availableExtras.length}
        />
      </FormGroup>
      <SaveEmailSubmitButtonGroup
        onClickSaveQuoteModal={onClickSaveQuoteModal}
        submitEnabled={isValid}
      />
      <FindOutMoreModal
        optionalExtras={availableExtras}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </form>
  );
};

export default OptionalExtrasStep;
